import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { CurriculumItemsAPI } from "@/lib/curriculum-items-api";
import { ExamStructureAPI } from "@/lib/exam-structure-api";
import { TYPE_LABELS, colorOf } from "@/lib/exam-tile-tokens";
import { AssessmentNodeDialog } from "@/components/exam/AssessmentNodeDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen, ClipboardCheck, FileText, FolderOpen, ArrowRight, Loader2, Info, PenLine, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const READ_ROLES = new Set([
  "hod", "principal", "inst_admin", "super_admin", "teacher",
  "campus_admin", "coordinator", "it_admin",
]);
const EDIT_ROLES = new Set([
  "super_admin", "inst_admin", "principal", "campus_admin",
  "hod", "coordinator", "it_admin",
]);

export default function Curriculum() {
  const { user } = useAuth();
  const canRead = (user?.roles || []).some((r) => READ_ROLES.has(r));
  const canEdit = (user?.roles || []).some((r) => EDIT_ROLES.has(r));
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [editingNode, setEditingNode] = useState(null); // { node, structure }

  const seqQ = useQuery({
    queryKey: ["curriculum-sequences"],
    queryFn: () => CurriculumItemsAPI.sequences(),
    enabled: canRead,
  });

  useEffect(() => {
    if (!selectedId && (seqQ.data?.structures || []).length) {
      setSelectedId(seqQ.data.structures[0].id);
    }
  }, [seqQ.data, selectedId]);

  if (!canRead) {
    return (
      <AppShell>
        <div className="p-8" data-testid="curriculum-page">
          <h1 className="font-display font-extrabold tracking-tight text-3xl">Curriculum</h1>
          <p className="text-sm text-muted-foreground mt-3">
            This module is for teaching staff. Ask your principal to assign you to a class to gain access.
          </p>
        </div>
      </AppShell>
    );
  }

  const selected = (seqQ.data?.structures || []).find((s) => s.id === selectedId);

  // Persist an edit to an assessment node via PATCH /exam-structure/:id
  const saveEditedNode = async (updated) => {
    const struct = editingNode?.structure;
    if (!struct) return;
    const newNodes = (struct.nodes || []).map((n) =>
      n.id === editingNode.node.id ? { ...n, ...updated, id: n.id, order: n.order } : n
    );
    try {
      await ExamStructureAPI.update(struct.id, {
        nodes: newNodes.map(({ item_count, ...rest }, i) => ({ ...rest, order: i })),
      });
      toast.success("Assessment updated");
      setEditingNode(null);
      qc.invalidateQueries({ queryKey: ["curriculum-sequences"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    }
  };

  const deleteNode = async (struct, node) => {
    if (!window.confirm(`Delete assessment "${node.name}"? This also clears any curriculum material attached to it.`)) return;
    const newNodes = (struct.nodes || []).filter((n) => n.id !== node.id);
    try {
      await ExamStructureAPI.update(struct.id, {
        nodes: newNodes.map(({ item_count, ...rest }, i) => ({ ...rest, order: i })),
      });
      toast.success("Assessment removed");
      qc.invalidateQueries({ queryKey: ["curriculum-sequences"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <AppShell>
      <div className="p-6 max-w-7xl mx-auto" data-testid="curriculum-page">
        <div className="mb-6">
          <h1 className="font-display font-extrabold tracking-tight text-3xl flex items-center gap-2.5">
            <BookOpen className="h-7 w-7 text-primary" /> Curriculum
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
            The assessment sequence you built in <span className="font-semibold text-foreground">Exam Structure</span> is mirrored here.
            Click any assessment tile to attach grade-, subject- &amp; chapter-tagged study material (PDF / DOC / image) that students &amp; teachers will use to prepare for it.
          </p>
        </div>

        {seqQ.isLoading ? (
          <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
        ) : (seqQ.data?.structures || []).length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
            {/* Sidebar — list of sequences */}
            <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="curr-seq-list">
              <div className="px-3 py-2.5 border-b border-border text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                Plans ({seqQ.data.total})
              </div>
              <ul className="divide-y divide-border max-h-[70vh] overflow-y-auto">
                {seqQ.data.structures.map((s) => {
                  const itemTotal = (s.nodes || []).reduce((a, n) => a + (n.item_count || 0), 0);
                  return (
                    <li
                      key={s.id}
                      onClick={() => setSelectedId(s.id)}
                      className={`px-3 py-2.5 cursor-pointer hover:bg-muted/50 ${selectedId === s.id ? "bg-primary/10 border-l-2 border-l-primary" : ""}`}
                      data-testid={`curr-seq-row-${s.id}`}
                    >
                      <div className="text-sm font-semibold truncate">{s.name}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <FileText className="h-2.5 w-2.5" />
                        {(s.nodes || []).length} assessments
                        {s.board && <span>· {s.board.toUpperCase()}</span>}
                        {s.academic_year && <span>· {s.academic_year}</span>}
                        {itemTotal > 0 && <Badge variant="secondary" className="text-[9px] h-4 px-1.5 ml-1">{itemTotal} files</Badge>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Tiles */}
            <div>
              {selected ? (
                <SequenceTiles
                  structure={selected}
                  canEdit={canEdit}
                  onOpen={(n) => navigate(`/curriculum/${selected.id}/assessment/${n.id}`)}
                  onEditNode={(n) => setEditingNode({ node: n, structure: selected })}
                  onDeleteNode={(n) => deleteNode(selected, n)}
                />
              ) : (
                <div className="border border-dashed border-border rounded-md bg-card p-16 text-center text-sm text-muted-foreground">
                  <ClipboardCheck className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  Select a plan on the left to view its assessment tiles.
                </div>
              )}
            </div>
          </div>
        )}

        {editingNode && (
          <AssessmentNodeDialog
            node={editingNode.node}
            onClose={() => setEditingNode(null)}
            onSave={saveEditedNode}
            testidPrefix="curr"
          />
        )}
      </div>
    </AppShell>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-border rounded-md bg-card p-12 text-center" data-testid="curr-empty">
      <ClipboardCheck className="h-10 w-10 mx-auto opacity-30 mb-3" />
      <h3 className="font-display font-bold text-lg">No assessment plans yet</h3>
      <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
        Head to <span className="font-semibold text-foreground">Exam Structure</span> first and build the assessment
        sequence (Unit Test 1, Half-Yearly, Final, etc.) for your campus. It will then appear here for you to attach study material.
      </p>
      <Button asChild className="mt-4" data-testid="curr-go-exam-structure">
        <a href="/exam-structure">Open Exam Structure <ArrowRight className="h-3.5 w-3.5 ml-1.5" /></a>
      </Button>
    </div>
  );
}

function SequenceTiles({ structure, canEdit, onOpen, onEditNode, onDeleteNode }) {
  const nodes = structure.nodes || [];
  return (
    <div className="space-y-4" data-testid="curr-tiles-pane">
      <div className="bg-card border border-border rounded-md p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <h2 className="font-display font-bold text-lg">{structure.name}</h2>
            <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
              {structure.board && <span>{structure.board.toUpperCase()}</span>}
              {structure.academic_year && <span>· {structure.academic_year}</span>}
              <span>· {nodes.length} assessments</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-primary/5 border border-primary/15 text-[11px] text-muted-foreground">
          <Info className="h-3.5 w-3.5 text-primary shrink-0" />
          Click a tile to upload study material. Hover any tile to edit or delete the assessment.
        </div>
      </div>

      {nodes.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground border border-dashed border-border rounded-md bg-card">
          This plan has no assessments yet. Add some in <a href="/exam-structure" className="text-primary hover:underline">Exam Structure</a>.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2.5" data-testid="curr-tiles">
          {nodes.map((n, idx) => (
            <Tile
              key={n.id || idx}
              node={n}
              index={idx}
              canEdit={canEdit}
              onOpen={() => onOpen(n)}
              onEdit={() => onEditNode(n)}
              onDelete={() => onDeleteNode(n)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Tile({ node, index, canEdit, onOpen, onEdit, onDelete }) {
  const c = colorOf(node);
  const count = node.item_count || 0;
  return (
    <div
      className={`relative ${c.bg} ${c.border} ${c.text} ${c.hoverBorder} border-2 rounded-lg shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition group`}
      data-testid={`curr-tile-${index}`}
      data-node-id={node.id}
    >
      <button
        type="button"
        onClick={onOpen}
        className={`w-full text-left p-3 focus:outline-none focus:ring-2 ${c.ring} focus:ring-offset-2 rounded-lg`}
        data-testid={`curr-tile-open-${index}`}
      >
        <div className="flex items-center justify-between mb-1.5">
          <span className={`${c.pill} text-white text-[9px] font-bold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center`}>
            {index + 1}
          </span>
          {count > 0 ? (
            <Badge className={`${c.pill} text-white border-0 text-[9px] h-4 px-1.5 hover:opacity-90`} data-testid={`curr-tile-count-${index}`}>
              <FolderOpen className="h-2.5 w-2.5 mr-0.5" /> {count}
            </Badge>
          ) : (
            <span className="text-[9px] uppercase tracking-wider opacity-50 font-bold">Empty</span>
          )}
        </div>

        <div className="text-[9px] uppercase tracking-wider font-bold opacity-70">
          {TYPE_LABELS[node.type] || node.type}
        </div>
        <div className="font-display font-bold text-sm leading-tight mt-0.5 line-clamp-2">{node.name}</div>
        <div className="text-[10px] mt-1.5 opacity-75 flex items-center gap-1.5 flex-wrap">
          {node.max_marks != null && <span>{node.max_marks} marks</span>}
          {node.weight_pct != null && <span>· {node.weight_pct}%</span>}
        </div>
        {node.scheduled_month && <div className="text-[10px] opacity-60 mt-0.5">{node.scheduled_month}</div>}

        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition pointer-events-none">
          <ArrowRight className="h-3.5 w-3.5" />
        </div>
      </button>

      {canEdit && (
        <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition z-10">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="h-5 w-5 rounded-full bg-white border border-border flex items-center justify-center shadow hover:bg-muted"
            title="Edit assessment"
            data-testid={`curr-tile-edit-${index}`}
          >
            <PenLine className="h-2.5 w-2.5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="h-5 w-5 rounded-full bg-white border border-rose-200 flex items-center justify-center shadow hover:bg-rose-50"
            title="Delete assessment"
            data-testid={`curr-tile-delete-${index}`}
          >
            <Trash2 className="h-2.5 w-2.5 text-rose-600" />
          </button>
        </div>
      )}
    </div>
  );
}
